import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronRight,
  Delete,
  Edit,
  Product
} from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { bundleDiscountedTotal } from "../bundleUtils";
import { ProductBundle, ServiceItemType } from "../types";
import { formatCompletePricingText, formatPrice } from "../utils";

const TRANSLATION_KEY = "settings.product.bundlesTab";

const CELL = "flex h-11 min-w-0 items-center px-2.5";

interface BundleCardProps {
  bundle: ProductBundle;
  serviceItemTypes: ServiceItemType[];
  onEdit: (bundle: ProductBundle) => void;
  onDelete: (bundle: ProductBundle) => void;
  defaultExpanded?: boolean;
  /** Controls the card from outside; without it the card keeps its own state. */
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const BundleCard: React.FC<BundleCardProps> = ({
  bundle,
  serviceItemTypes,
  onEdit,
  onDelete,
  defaultExpanded = false,
  isExpanded: controlledExpanded,
  onExpandedChange
}) => {
  const { t } = useTranslation();
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    useState(defaultExpanded);
  const isExpanded = controlledExpanded ?? uncontrolledExpanded;

  const toggleExpanded = () => {
    setUncontrolledExpanded(!isExpanded);
    onExpandedChange?.(!isExpanded);
  };

  const typeName = (typeId: number | null) =>
    serviceItemTypes.find(({ id }) => id === typeId)?.name ?? "";

  const discountAmount = bundle.discountAmount ?? 0;
  const discountLabel = bundle.isDiscountPercentage
    ? t(`${TRANSLATION_KEY}.percentageDiscount`, {
        // Past the default 3 decimals, so a stored 12.3456 isn't shown rounded.
        percentage: discountAmount.toLocaleString(undefined, {
          maximumFractionDigits: 6
        })
      })
    : t(`${TRANSLATION_KEY}.amountDiscount`, {
        amount: formatPrice(discountAmount)
      });

  return (
    <div
      className="w-full overflow-hidden rounded-2xl border-[0.5px] border-border-widget-card bg-surface-widget-card"
      data-test={`product-bundle-card-${bundle.id}`}
    >
      <div className="group flex min-h-[76px] items-center gap-6 px-4 py-3 transition-colors hover:bg-surface-hover-soft">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-md"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          data-test={`product-bundle-card-toggle-${bundle.id}`}
        >
          <IconContainer
            as="span"
            size="sm"
            color="secondary"
            className="m-1 shrink-0"
            aria-hidden="true"
          >
            {isExpanded ? <ChevronDown /> : <ChevronRight />}
          </IconContainer>
          <span className="flex min-w-0 flex-col">
            <Text as="span" variant="body" className="block truncate">
              {bundle.name}
            </Text>
            {bundle.description && (
              <Text
                as="span"
                variant="bodySecondary"
                className="block truncate"
              >
                {bundle.description}
              </Text>
            )}
          </span>
        </button>
        <div className="flex shrink-0 gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <IconButton
            size="md"
            aria-label={t(`${TRANSLATION_KEY}.editBundle`, {
              name: bundle.name
            })}
            onClick={() => onEdit(bundle)}
            data-test={`product-bundle-card-edit-${bundle.id}`}
          >
            <Edit />
          </IconButton>
          <IconButton
            size="md"
            destructive
            // Filled as in the design, not only on hover.
            className="bg-surface-destructive-hover data-[destructive=true]:data-[disabled=false]:border-[var(--red-opacity-l)]"
            aria-label={t(`${TRANSLATION_KEY}.deleteBundle`, {
              name: bundle.name
            })}
            onClick={() => onDelete(bundle)}
            data-test={`product-bundle-card-delete-${bundle.id}`}
          >
            <Delete />
          </IconButton>
        </div>
      </div>
      {isExpanded && (
        <div data-test={`product-bundle-card-table-${bundle.id}`}>
          <div className="flex h-9 items-end border-y-[0.5px] border-border-border1 pb-1">
            <div className="w-4 shrink-0" />
            <Text variant="smBodySecondary" className="w-[180px] px-2.5">
              {t(`${TRANSLATION_KEY}.table.serviceItem`)}
            </Text>
            <Text variant="smBodySecondary" className="w-[140px] px-2.5">
              {t(`${TRANSLATION_KEY}.table.type`)}
            </Text>
            <Text variant="smBodySecondary" className="flex-1 px-2.5">
              {t(`${TRANSLATION_KEY}.table.pricing`)}
            </Text>
            <Text variant="smBodySecondary" className="w-20 px-2.5 text-right">
              {t(`${TRANSLATION_KEY}.table.quantity`)}
            </Text>
          </div>
          {bundle.items.map((item) => (
            <div
              key={item.serviceItemId}
              className="flex border-b-[0.5px] border-border-border1"
              data-test={`product-bundle-item-${item.serviceItemId}`}
            >
              <div className="w-4 shrink-0" />
              <div className={cn(CELL, "w-[180px] gap-2")}>
                <IconContainer size="sm" color="secondary" className="shrink-0">
                  <Product />
                </IconContainer>
                <Text variant="body" className="truncate">
                  {item.serviceItemName}
                </Text>
              </div>
              <div className={cn(CELL, "w-[140px]")}>
                <Text variant="body" className="truncate capitalize">
                  {typeName(item.serviceItemType)}
                </Text>
              </div>
              <div className={cn(CELL, "flex-1")}>
                <Text variant="body" className="truncate">
                  {formatCompletePricingText(
                    item.price,
                    item.pricingModel ?? 0,
                    item.billingFrequency,
                    item.serviceItemPeriods,
                    t,
                    item.chargeType
                  )}
                </Text>
              </div>
              <div className={cn(CELL, "w-20 justify-end")}>
                <Text variant="body">{item.quantity.toLocaleString()}</Text>
              </div>
            </div>
          ))}
          {discountAmount > 0 && (
            <div
              className="flex"
              data-test={`product-bundle-card-discount-${bundle.id}`}
            >
              <div className="w-4 shrink-0" />
              <div className={cn(CELL, "flex-1")}>
                <Text variant="bodySecondary">{discountLabel}</Text>
              </div>
              <div className={cn(CELL, "gap-1")}>
                <Text variant="bodyTertiary" className="line-through">
                  {formatPrice(bundle.total)}
                </Text>
                <Text variant="body">
                  {formatPrice(
                    bundle.discountedTotal ??
                      bundleDiscountedTotal(
                        bundle.total,
                        discountAmount,
                        bundle.isDiscountPercentage ?? false
                      )
                  )}
                </Text>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
